import { useState } from "react";

import { Check, ChevronLeft } from "lucide-react";

import { StepHeader } from "@/components/onboarding/StepHeader";
import { reboltToast } from "@/components/toasts";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import type { CompleteOnboardingFormData } from "@/lib/zod-schemas/onboarding";
import type { OnboardingStepProps } from "@/types/onboarding";
import type { SerpAPI } from "@/types/serpapi";

export function CompanySelectionStep({
  form,
  onNext,
  onBack,
  canGoBack,
  isOptional,
}: OnboardingStepProps<CompleteOnboardingFormData>) {
  const serpApiData = form.getValues("serpApiData");
  const [selectedCompany, setSelectedCompany] = useState<Partial<SerpAPI.PlaceResult> | null>(null);

  // empty state
  if (serpApiData.length === 0) {
    return (
      <div className="h-full flex flex-col justify-start lg:justify-center px-5 lg:px-40 py-6 lg:py-0 w-full">
        {canGoBack && (
          <Button
            type="button"
            variant="rebolt-outline"
            size="sm"
            onClick={onBack}
            className="tracking-tight w-fit lg:mb-12 mb-4 gap-2 p-2.5 lg:px-6 lg:py-2"
            iconPosition="start"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden lg:inline-block">Back</span>
          </Button>
        )}
        <StepHeader
          title="We couldn't find your business."
          subtitle="Don't worry. You'll be able to fill in this info later."
        />

        <div className="lg:mt-9 mt-auto w-full flex gap-4 items-center">
          <Button
            type="button"
            variant="rebolt-secondary"
            size="md"
            className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2"
            onClick={onNext}
          >
            Continue
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col justify-start lg:justify-center px-5 lg:px-40 py-6 lg:py-0 w-full">
      {canGoBack && (
        <Button
          type="button"
          variant="rebolt-outline"
          size="sm"
          onClick={onBack}
          className="tracking-tight w-fit lg:mb-12 mb-4 gap-2 p-2.5 lg:px-6 lg:py-2"
          iconPosition="start"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="hidden lg:inline-block">Back</span>
        </Button>
      )}
      <StepHeader
        title="Here's what we found"
        subtitle="Select your company to import details. Don't see it? Search again or enter manually."
      />

      <div className="mt-6 lg:mt-12 w-full flex flex-col">
        <div className="lg:p-3 p-2 bg-white rounded-2xl max-h-[375px] overflow-y-auto flex flex-col">
          {serpApiData.map((item, index) => (
            <div key={item.placeId} className="flex flex-col">
              <div
                className={cn(
                  "flex gap-4 lg:p-3 p-2 rounded-lg justify-between items-center hover:bg-clay-200 transition-colors cursor-pointer select-none lg:min-h-20 min-h-12",
                  selectedCompany?.placeId === item.placeId && "bg-clay-100"
                )}
                onClick={() => {
                  if (selectedCompany?.placeId === item.placeId) {
                    setSelectedCompany(null);
                  } else {
                    setSelectedCompany(item);
                  }
                }}
              >
                <div className="flex flex-col gap-2 justify-center">
                  <h3 className="font-clash font-medium lg:text-xl text-base">{item.name}</h3>
                  {item.address && <p className="text-ash-500 font-medium lg:text-base text-sm">{item.address}</p>}
                </div>
                {selectedCompany?.placeId === item.placeId && <Check className="w-6 h-6 text-mint-400" />}
              </div>
              {index < serpApiData.length - 1 && <hr className="h-px w-full border-ash-300 my-3" />}
            </div>
          ))}
        </div>
      </div>

      <div className="flex gap-4 lg:mt-12 mt-auto">
        <Button
          type="button"
          variant="rebolt-secondary"
          size="md"
          className="justify-center lg:max-w-fit tracking-tight lg:py-3 py-2"
          onClick={() => {
            if (!selectedCompany) {
              reboltToast.error("Please select a company to import details");
              return;
            }
            form.setValue("companyPlaceId", selectedCompany.placeId);
            form.setValue("companyName", selectedCompany.name);
            form.setValue("websiteUrl", selectedCompany.website);
            return onNext();
          }}
        >
          Import details
        </Button>
        {isOptional && (
          <Button type="button" variant="rebolt-link" size="link" className="text-sm lg:text-base" onClick={onNext}>
            Enter manually
          </Button>
        )}
      </div>
    </div>
  );
}
